import { useMemo, useState } from "react";
import primaryComponents from "../../../primaryComponents";
import secondaryComponents from "../..";
import { useTable } from "react-table";
import { truncateString } from "../../../../helpers";
import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import { DeleteIcon, UpDownArrow } from "../../../../assets/svgIcons";
import { PricingTooltip } from "./PricingTooltip";
import styles from "./styles.module.scss";
import { useProductSelect } from "../../../../hooks/useProductSelect";
import { useUrlSearch } from "../../../../hooks/useUrlSearch";

export const useServiceTable = ({
  services = [],
  formData = {},
  setFormData = () => {},
}) => {
  const navigate = useNavigate();
  const { search, filterByKey } = useUrlSearch();
  const [selectedService, setSelectedService] = useState(null);
  const { handleCheckBoxUpdate, handleCheckAll } = useProductSelect({
    formData,
    setFormData,
  });

  const columns = useMemo(
    () => [
      {
        Header: (
          <primaryComponents.Checkbox
            name="selectAllService"
            id="selectAllService"
            labelClassName={styles.selectAll}
            isChecked={formData?.is_select_all}
            onChange={() => handleCheckAll(services)}
          >
            <span className="d-block mt-1 smallest-text-size gray--text">
              Service ID
            </span>
          </primaryComponents.Checkbox>
        ),
        accessor: "id",
        Cell: ({ cell: { value }, row }) => {
          const idKey = `${row?.original?.id}`;
          return (
            <primaryComponents.Checkbox
              name="checkedService"
              id={idKey}
              isChecked={
                formData?.selected_ids?.includes(idKey) ||
                (formData?.is_select_all &&
                  !formData?.exclude_selected_ids?.includes(idKey))
              }
              onChange={() => {
                handleCheckBoxUpdate(idKey);
              }}
            >
              <span className="d-block mt-1 smallest-text-size gray--text">
                {value}
              </span>
            </primaryComponents.Checkbox>
          );
        },
      },
      {
        Header: (
          <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>Name</span>
            <secondaryComponents.ToolTipV2
              tipText={
                search.get("order_by") === "name"
                  ? "Sort by descending order"
                  : search.get("order_by") === "-name"
                    ? "Sort by ascending order"
                    : "Sort by ascending order"
              }
              position="right"
            >
              <span
                className="cursor-pointer"
                onClick={() =>
                  filterByKey(
                    "order_by",
                    search.get("order_by") === "name" ? "-name" : "name",
                  )
                }
              >
                <UpDownArrow />
              </span>
            </secondaryComponents.ToolTipV2>
          </span>
        ),
        accessor: "name",
        Cell: ({ cell: { value } }) => truncateString(value, 300),
      },
      {
        Header: "Selling Price",
        Cell: ({ cell: { row } }) => {
          const pricings =
            row?.original?.["store_service_properties"]?.[0]?.pricings;

          const store_currency = row?.original?.stores?.find(
            (store) => `${store?.id}` === `${search.get("store_id")}`,
          )?.currency_code;

          return (
            <PricingTooltip
              name={row?.original?.name}
              pricings={pricings}
              store_currency={store_currency}
            />
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ cell: { row } }) => (
          <span className="d-flex align-items-center" style={{ gap: "10px" }}>
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_edit_or_update_product"
            >
              <primaryComponents.Button
                classNames={"btn btn--primary smallBtn"}
                onClick={() =>
                  navigate(
                    pathConstants.INVENTORY_SERVICE_EDIT({
                      id: row?.original?.id,
                    }),
                  )
                }
              >
                Update
              </primaryComponents.Button>
            </secondaryComponents.NavigateWithPermission>

            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_delete_products"
            >
              <span
                className="cursor-pointer"
                onClick={() => setSelectedService(row?.original)}
              >
                <DeleteIcon />
              </span>
            </secondaryComponents.NavigateWithPermission>
          </span>
        ),
      },
    ],
    [formData],
  );

  const instance = useTable({
    columns,
    data: services,
  });

  return { instance, selectedService, setSelectedService };
};
